// RepoCard.tsx — one watched repository as a card: what it is, how current its
// documentation is, and (for an admin) the two controls worth having on a
// listing — queue a run, or stop watching it for a while.
//
// A card rather than a table row because the question this page answers is
// "which repositories are behind", and that is read from a status word, a
// timestamp and a document count sitting together, not from scanning a column.
// Removing a repository is deliberately not here: it is destructive, it needs
// a confirmation, and a listing is where a misclick is most likely — it lives
// on the repository's own page instead.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Table from 'react-bootstrap/Table';
import { BsArrowClockwise, BsBook, BsPauseFill, BsPlayFill } from 'react-icons/bs';
import { StatusBadge } from 'common';
import { pauseRepo, reindexRepo } from '../api';
import type { Repo } from '../api';
import { EM_DASH, absoluteTime, relativeTime, shortSha } from '../format';
import { RepoStatusBadge } from './StatusBadges';

export default function RepoCard({ repo, admin, onChanged }: {
  repo: Repo;
  admin: boolean;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Both controls are one shape: run a mutation, surface what it said, and ask
  // the page above to re-read. The page owns the list, so it owns the reload —
  // a card that patched its own copy would disagree with its siblings the
  // moment the daemon picked the run up.
  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    // h-100 so a row of cards keeps a flat bottom edge whatever each
    // repository's error text weighs, and the footers line up across the grid.
    <Card className="h-100">
      <Card.Header className="d-flex align-items-center justify-content-between gap-2">
        <Link to={`/repos/${repo.owner}/${repo.name}`} className="text-decoration-none text-truncate">
          {repo.full_name}
        </Link>
        <span className="d-flex align-items-center gap-2 flex-shrink-0">
          {!repo.enabled && <StatusBadge tone="warning">paused</StatusBadge>}
          <RepoStatusBadge status={repo.status} />
        </span>
      </Card.Header>
      <Card.Body className="d-flex flex-column gap-2">
        {error && <Alert variant="danger" className="m-0 py-2 small">{error}</Alert>}
        {/* The repository's own last error, as opposed to the one this card's
            controls just produced. It survives page reloads and is the reason
            a repo reads ERROR, so it belongs in the body rather than in a
            transient banner. */}
        {repo.last_error && (
          <Alert variant="warning" className="m-0 py-2 small">
            <Alert.Heading className="h6 m-0">Last run failed</Alert.Heading>
            {repo.last_error}
          </Alert>
        )}
        {/* The field-rows pattern: label left, value right, no chrome. */}
        <Table size="sm" borderless className="m-0">
          <tbody>
            <tr>
              <td className="text-muted ps-0">Branch</td>
              <td className="text-end pe-0"><code>{repo.default_branch || EM_DASH}</code></td>
            </tr>
            <tr>
              <td className="text-muted ps-0">Indexed</td>
              <td className="text-end pe-0" title={absoluteTime(repo.last_indexed_at)}>
                <code>{shortSha(repo.last_indexed_sha)}</code>
                <span className="text-muted"> · {relativeTime(repo.last_indexed_at)}</span>
              </td>
            </tr>
            <tr>
              <td className="text-muted ps-0">Documents</td>
              <td className="text-end pe-0">{repo.doc_count}</td>
            </tr>
          </tbody>
        </Table>
      </Card.Body>
      {/* Actions on the bottom edge, in every card whether or not this session
          may write: the documentation link is always there, so the row's
          footers stay in rhythm for a reader who is not an admin. */}
      <Card.Footer className="d-flex flex-wrap align-items-center gap-2">
        <Link
          to={`/docs/${repo.owner}/${repo.name}`}
          className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-2"
        >
          <BsBook /> Docs
        </Link>
        {admin && (
          <>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={busy || !repo.enabled}
              title={repo.enabled
                ? 'Queue a run over the paths that changed since the last indexed commit'
                : 'Resume watching this repository first'}
              onClick={() => void act(() => reindexRepo(repo.owner, repo.name, false))}
              className="d-inline-flex align-items-center gap-2"
            >
              <BsArrowClockwise /> Reindex
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={busy}
              title={repo.enabled
                ? 'Stop reacting to pushes on this repository'
                : 'Watch this repository again'}
              onClick={() => void act(() => pauseRepo(repo.owner, repo.name, !repo.enabled))}
              className="d-inline-flex align-items-center gap-2"
            >
              {repo.enabled ? <><BsPauseFill /> Pause</> : <><BsPlayFill /> Resume</>}
            </Button>
          </>
        )}
      </Card.Footer>
    </Card>
  );
}
